import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import {
  IFieldArrayData,
  IFieldData,
  IFieldGroupNameData,
} from '../../models/application-form-data.model';
import { IIremboFileUpload } from '../../models/irembo-file-upload-response.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, Subscription, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ToastService } from '../../services/toast/toast.service';
import { IIrembogovBasicLabelKeyPair } from '../../models/irembogov-basic-label-keypair.model';
import {
  fieldCardUtilDownloadFile,
  fieldCardUtilGetImagePath,
  fieldCardUtilGetMultiLineValues,
  fieldCardUtilGetSingleLineValue,
  fieldCardUtilIsImage,
  fieldCardUtilTrackByFn,
  TSingleLineValue,
} from '../component-utils/application-form-field-card-preview.util';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'irembogov-non-logeddin-application-form-field-card',
  templateUrl: './non-loggedin-application-form-field-card.component.html',
  styleUrls: ['./non-loggedin-application-form-field-card.component.scss'],
})
export class NonLoggedInApplicationFormFieldCardComponent
  implements OnDestroy, OnInit
{
  @Input() group: IFieldGroupNameData | undefined;
  @Input() baseUrl?: any;
  @Input() applicationPath: string | undefined;
  @Input() applicationNumber: string | undefined;
  @Input() otp: string | undefined;
  selectedAttachment: IIremboFileUpload | undefined;
  filePath: string | undefined;
  private subscriptions = new Subscription();

  imagesSourceList: Array<string | null> = [];
  base64Data: string | undefined;
  loadingPreview = false;

  multiLineValueFieldTypes: string[] = ['customdoubleidinput'];

  constructor(
    public readonly modalService: NgbModal,
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer,
    private readonly translateService: TranslateService,
    private readonly toast: ToastService,
    private readonly cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.applicationNumber && this.otp) {
      this.getRemoteImagePaths(this.applicationNumber, this.otp);
    }
    this.preloadTranslations();
  }

  async preloadTranslations(): Promise<void> {
    // Call a translate service with a mock word to ensure it's loaded.
    await this.translateService.instant('sample');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  trackByFn(index: number, item: IFieldData | IFieldArrayData) {
    return fieldCardUtilTrackByFn(index);
  }

  getSingleLineValue(field: IFieldData | undefined): string | null {
    const singleLineValue: TSingleLineValue = fieldCardUtilGetSingleLineValue(
      field,
      this.multiLineValueFieldTypes
    );

    if (Array.isArray(singleLineValue)) {
      return singleLineValue
        .map((item: string) =>
          item ? this.translateService.instant(item) : item
        )
        .join(', ');
    }

    return singleLineValue
      ? this.translateService.instant(String(singleLineValue))
      : singleLineValue;
  }

  getMultiLineValues(
    field: IFieldData | undefined
  ): IIrembogovBasicLabelKeyPair<string | null>[] {
    return fieldCardUtilGetMultiLineValues(
      field,
      this.multiLineValueFieldTypes
    );
  }

  getAttachmentValue(dataValue: unknown): IIremboFileUpload {
    return dataValue as IIremboFileUpload;
  }

  previewAttachment(
    content: TemplateRef<unknown>,
    file: unknown,
    index: number
  ) {
    this.selectedAttachment = file as IIremboFileUpload;
    this.filePath = '';
    this.loadingPreview = true;

    if (!this.selectedAttachment.fileDownloadName) {
      if (this.isImage(this.selectedAttachment.fileType)) {
        this.filePath = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.selectedAttachment.content as string
        ) as string;
      } else {
        this.base64Data = this.selectedAttachment.content as string;
      }
      this.loadingPreview = false;
    }

    if (
      this.selectedAttachment.fileDownloadName &&
      this.applicationNumber &&
      this.otp
    ) {
      if (this.isImage(this.selectedAttachment.fileType)) {
        this.filePath = this.imagesSourceList[index] as string;
        this.loadingPreview = false;
      } else {
        this.subscriptions.add(
          this.getFileFromUrl(
            this.applicationNumber,
            this.selectedAttachment.fileDownloadName,
            this.otp
          ).subscribe({
            next: res => {
              const reader = new FileReader();
              reader.readAsDataURL(res);
              reader.onload = () => {
                this.filePath = this.sanitizer.bypassSecurityTrustResourceUrl(
                  reader.result as string
                ) as string;
                this.base64Data = reader.result as string;
                this.loadingPreview = false;
              };
            },
            error: () => {
              this.toast.show({ body: `Error loading preview`, type: 'error' });
              this.loadingPreview = false;
            },
          })
        );
      }
    }
    this.modalService.open(content, {
      ariaLabelledBy: 'preview-document',
      size: 'xl',
    });
  }

  isImage(mimeType: unknown) {
    return fieldCardUtilIsImage(mimeType);
  }

  getImagePath(file: unknown): string {
    return fieldCardUtilGetImagePath(file);
  }

  getRemoteImagePaths(applicationNumber: string, otp: string) {
    this.group?.fieldGroup?.forEach(field => {
      if (field?.fieldType === 'customfileupload') {
        const value = field?.fieldValue as IIremboFileUpload;
        if (this.isImage(value.fileType) && value.fileDownloadName) {
          this.subscriptions.add(
            this.loadImageAsync(
              applicationNumber,
              value.fileDownloadName,
              otp
            ).subscribe({
              next: res => {
                this.imagesSourceList.push(res);
              },
            })
          );
        } else {
          this.imagesSourceList.push(null);
        }
      } else {
        this.imagesSourceList.push(null);
      }
    });
  }

  getFileFromUrl(
    applicationNumber: string,
    fileDownloadName: string,
    otp: string
  ) {
    const url = `${this.baseUrl}${this.applicationPath}${applicationNumber}/attachment-by-otp`;
    const decodedFileName = decodeURIComponent(fileDownloadName);
    const encodedFileName = encodeURIComponent(decodedFileName);
    const fullUrl = `${url}?fileName=${encodedFileName}&otp=${encodeURIComponent(
      otp
    )}`;
    return this.http.get(fullUrl, { responseType: 'blob' });
  }

  loadImageAsync(
    applicationNumber: string,
    fileDownloadName: string,
    otp: string
  ) {
    return this.getFileFromUrl(applicationNumber, fileDownloadName, otp).pipe(
      switchMap(res => {
        return new Observable<string>(observer => {
          const reader = new FileReader();
          reader.readAsDataURL(res);
          reader.onload = () => {
            observer.next(
              this.sanitizer.bypassSecurityTrustResourceUrl(
                reader.result as string
              ) as string
            );
            observer.complete();
          };
        });
      })
    );
  }

  downloadFile(base64String: string, fileName: string) {
    fieldCardUtilDownloadFile(base64String, fileName);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
